import { logEvent, setUserId } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import * as Sentry from '@sentry/react';
import {
  TelemetryEventName,
  type TelemetryEventNameValue,
  type TelemetryEvent,
  type FlowStartParams,
  type FlowCompleteParams,
  type FlowErrorParams,
} from '@little-chef/domain';
import { eventBuffer } from './eventBuffer';
import { getConsentService } from './granularConsentService';

/**
 * TelemetryService
 * ================
 * Frontend implementation of ITelemetryService.
 * All methods are fail-silent — they never throw or block the UI.
 *
 * Usage:
 *   export const telemetryService = new TelemetryService(initAnalytics());
 */
export class TelemetryService {
  private readonly sessionId: string;
  private readonly analytics: Analytics;

  constructor(analytics: Analytics) {
    this.analytics = analytics;
    this.sessionId = crypto.randomUUID();
  }

  getSessionId(): string {
    return this.sessionId;
  }

  trackScreenView(screenName: string): void {
    this.trackEvent(TelemetryEventName.SCREEN_VIEW, { screen_name: screenName });
  }

  trackFlowStart(params: FlowStartParams): void {
    this.trackEvent(TelemetryEventName.FLOW_START, params as unknown as Record<string, unknown>);
  }

  trackFlowComplete(params: FlowCompleteParams): void {
    this.trackEvent(TelemetryEventName.FLOW_COMPLETE, params as unknown as Record<string, unknown>);
  }

  trackFlowError(params: FlowErrorParams): void {
    this.trackEvent(TelemetryEventName.FLOW_ERROR, params as unknown as Record<string, unknown>);
  }

  /**
   * Generic event tracker. Call this for domain-specific events not covered
   * by the typed methods above (e.g. recipe_deleted, cookbook_created, etc.).
   */
  trackEvent(name: TelemetryEventNameValue, params: Record<string, unknown> = {}): void {
    try {
      if (import.meta.env.DEV) {
        console.log('[Telemetry]', name, params);
      }
      logEvent(this.analytics, name as string, params);
    } catch (e) {
      // Buffer the event for retry on next session start
      try {
        eventBuffer.enqueue({
          id: crypto.randomUUID(),
          target: 'analytics',
          payload: {
            name,
            sessionId: this.sessionId,
            environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'dev') as
              | 'dev'
              | 'staging'
              | 'prod',
            timestamp: Date.now(),
            params: params as unknown as TelemetryEvent['params'],
          },
          createdAt: Date.now(),
          retryCount: 0,
        });
      } catch {
        /* storage quota — silently discard */
      }
      if (import.meta.env.DEV) {
        console.warn('[Telemetry] logEvent failed, buffered', name, e);
      }
    }
  }

  identifyUser(firebaseUid: string): void {
    try {
      const consentService = getConsentService(this.analytics);
      consentService.setCurrentUid(firebaseUid);
      Sentry.setUser({ id: firebaseUid });
      if (consentService.isAnalyticsAccepted()) {
        setUserId(this.analytics, firebaseUid);
      }
      if (import.meta.env.DEV) {
        console.log('[Telemetry] identifyUser', {
          uid: firebaseUid,
          consent: consentService.getState(),
          userIdSet: consentService.isAnalyticsAccepted(),
        });
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[Telemetry] identifyUser failed', e);
    }
  }

  clearUser(): void {
    try {
      Sentry.setUser(null);
      setUserId(this.analytics, null);
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[Telemetry] clearUser failed', e);
    }
  }

  async flushBuffer(): Promise<void> {
    await eventBuffer.flush(async (bufferedEvent) => {
      logEvent(
        this.analytics,
        bufferedEvent.payload.name as string,
        bufferedEvent.payload.params as unknown as Record<string, unknown>,
      );
    });
  }
}

/**
 * No-op stub used when telemetry is not initialised (e.g. in tests or before app bootstrap).
 * Allows components that call getTelemetryService() to run without throwing.
 */
const noopStub: TelemetryService = {
  getSessionId: () => '',
  trackScreenView: () => {},
  trackFlowStart: () => {},
  trackFlowComplete: () => {},
  trackFlowError: () => {},
  trackEvent: () => {},
  identifyUser: () => {},
  clearUser: () => {},
  flushBuffer: () => Promise.resolve(),
} as unknown as TelemetryService;

/**
 * Singleton telemetry service instance.
 * Initialised lazily — use getTelemetryService() in components/hooks.
 * When called without analytics (e.g. in tests), returns a no-op stub so callers do not throw.
 */
let _instance: TelemetryService | null = null;

export function getTelemetryService(analytics?: Analytics): TelemetryService {
  if (!_instance) {
    if (!analytics) return noopStub;
    _instance = new TelemetryService(analytics);
  }
  return _instance;
}
